<template>
  <!-- 입고처리 — 핸드폰 촬영(QR) 버튼 + 들어온 사진 썸네일 + 사이즈 판독/저장.
       compact: 버튼만(목록 행). kind: QR 을 열었을 때 핸드폰에서 먼저 선택될 사진 종류 -->
  <div class="ipp">
    <div class="d-flex align-items-center flex-wrap" style="gap:6px">
      <b-button :size="compact ? 'sm' : ''" variant="outline-dark" @click="openQr" :disabled="issuing" class="text-nowrap">
        <b-spinner v-if="issuing" small></b-spinner> 📱 手机拍照<small class="ml-1">(핸드폰 촬영)</small>
      </b-button>
      <!-- 需跟客户确认 사진은 PC 에서 바로 여러 장 올릴 수도 있다(2026-10-04 YZY) -->
      <template v-if="confirmMode">
        <b-button variant="outline-secondary" :disabled="uploading" class="text-nowrap" @click="$refs.pcFiles.click()">
          <b-spinner v-if="uploading" small></b-spinner> 🖼 上传照片<small class="ml-1">(사진 업로드)</small>
        </b-button>
        <input ref="pcFiles" type="file" accept="image/*" multiple style="display:none" @change="onPcFiles">
      </template>
      <b-badge v-if="compact && shownPhotos.length" variant="info">📷{{ shownPhotos.length }}</b-badge>
    </div>

    <!-- 들어온 사진 (작업 모달) -->
    <div v-if="!compact && shownPhotos.length" class="ipp__grid mt-2">
      <div v-for="(p, i) in shownPhotos" :key="p.url + i" class="ipp__cell">
        <a :href="p.url" target="_blank" rel="noopener"><img :src="p.url" alt=""></a>
        <span class="ipp__tag" :class="'ipp__tag--' + kindCode(p.종류)">{{ kindCn(p.종류) }}</span>
        <span v-if="p.이전입고" class="ipp__prev" title="以前入库的照片(이전 입고 사진)">以前</span>
        <b-button v-if="showSize && p.종류 === '사이즈사진'" size="sm" variant="light" class="ipp__read" :disabled="reading" @click="readSize([p.url])">🔍 识别</b-button>
        <button type="button" class="ipp__del" title="删除(삭제)" :disabled="removing === p.url" @click="removePhoto(p)">✕</button>
      </div>
    </div>

    <!-- 사이즈: 판독값은 칸에만 채운다. 저장은 담당자가 확인하고 保存 -->
    <div v-if="showSize" class="d-flex align-items-center flex-wrap mt-2" style="gap:4px;font-size:0.9rem">
      <strong class="mr-1">📏 尺寸<small>(사이즈, 厘米cm·公斤kg)</small></strong>
      <!-- 판독이 확실하지 않은 칸은 값은 채우되 노란 테두리 + 「请确认」 — 손으로 고치면 표시가 사라진다 -->
      <template v-for="f in sizeFields">
        <span :key="f.key + 'l'" :class="{ 'ml-1': f.key === 'weightKg' }">{{ f.label }}</span>
        <input :key="f.key + 'i'" type="number" min="0" :step="f.step" class="form-control form-control-sm" :class="{ 'ipp__unsure': unsure[f.key] }"
               :style="{ width: f.key === 'weightKg' ? '75px' : '70px' }" v-model.number="size[f.key]" @input="unsure[f.key] = false">
        <small v-if="unsure[f.key]" :key="f.key + 'w'" class="text-warning font-weight-bold">请确认<small>(확인 필요)</small></small>
      </template>
      <b-button size="sm" variant="primary" @click="saveSize" :disabled="saving || reading">
        <b-spinner v-if="saving" small></b-spinner> 💾 保存
      </b-button>
      <span v-if="reading" class="text-info"><b-spinner small></b-spinner> 识别中...<small>(판독 중)</small></span>
      <span v-else-if="readMsg" :class="readFail ? 'text-danger' : 'text-success'">{{ readMsg }}</span>
    </div>

    <!-- QR 모달 -->
    <b-modal v-model="showQr" title="📱 手机拍照(핸드폰 촬영)" hide-footer size="md" @hidden="refresh">
      <div class="text-center">
        <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR" style="width:260px;height:260px;image-rendering:pixelated">
        <div class="small text-muted mt-1">用手机扫码拍照上传，有效期2小时<small class="d-block">(핸드폰으로 스캔해 촬영·업로드, 2시간 유효)</small></div>
        <div class="small mt-1" style="word-break:break-all"><a :href="qrUrl" target="_blank" rel="noopener">{{ qrUrl }}</a></div>
        <div class="mt-2"><strong>{{ item && item.상품명 }}</strong></div>
        <div v-if="shownPhotos.length" class="ipp__grid mt-2 justify-content-center">
          <div v-for="(p, i) in shownPhotos" :key="'q' + p.url + i" class="ipp__cell">
            <a :href="p.url" target="_blank" rel="noopener"><img :src="p.url" alt=""></a>
            <span class="ipp__tag" :class="'ipp__tag--' + kindCode(p.종류)">{{ kindCn(p.종류) }}</span>
            <span v-if="p.이전입고" class="ipp__prev" title="以前入库的照片(이전 입고 사진)">以前</span>
            <button type="button" class="ipp__del" title="删除(삭제)" :disabled="removing === p.url" @click="removePhoto(p)">✕</button>
          </div>
        </div>
        <div v-else class="small text-muted mt-2">等待手机上传...<small>(핸드폰 업로드 대기 중)</small></div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import qrcode from 'qrcode-generator';
import { uploadImportPhoto } from '@/utils/importPhotoS3';

// 저장 키(value)는 그대로, 표기만 — 입고사진 = 商品及包装(상품·포장 사진)
const KINDS = [
  { value: '입고사진', code: 'in', cn: '商品及包装' },
  { value: '사이즈사진', code: 'size', cn: '尺寸' },
  { value: '오류사진', code: 'err', cn: '错误' },
  // 需跟客户确认(고객 확인 필요) 사진 — 2026-10-04 YZY
  { value: '확인사진', code: 'cf', cn: '需确认' }
];
const POLL_MS = 4000;
// 사이즈 칸 ↔ 판독 결과 키
const SIZE_FIELDS = [
  { key: 'sizeX', read: 'length', label: '长(cm)', step: 0.1 },
  { key: 'sizeY', read: 'width', label: '宽(cm)', step: 0.1 },
  { key: 'sizeZ', read: 'height', label: '高(cm)', step: 0.1 },
  { key: 'weightKg', read: 'weight', label: '重量(kg)', step: 0.01 }
];
const READ_MAX = 6; // 서버 IMPORT_SIZE_READ_MAX

export default {
  name: 'ImportPhotoPanel',
  props: {
    // Reqpurchase 문서(입고처리 검색 결과 행). 사진·사이즈를 받으면 이 객체에 반영한다
    item: { type: Object, default: null },
    kind: { type: String, default: '입고사진' },
    compact: { type: Boolean, default: false },
    // true 면 kind 종류 사진만 보인다(需跟客户确认 칸 — 입고사진 패널과 겹쳐 보이지 않게)
    onlyKind: { type: Boolean, default: false }
  },
  data() {
    return {
      issuing: false,
      showQr: false,
      qrUrl: '',
      qrDataUrl: '',
      timer: null,
      seen: null,
      size: { sizeX: 0, sizeY: 0, sizeZ: 0, weightKg: 0 },
      unsure: { sizeX: false, sizeY: false, sizeZ: false, weightKg: false },
      sizeFields: SIZE_FIELDS,
      readSeq: 0,
      reading: false,
      readMsg: '',
      readFail: false,
      saving: false,
      removing: '',
      uploading: false
    };
  },
  computed: {
    photos() {
      return (this.item && Array.isArray(this.item.입고사진)) ? this.item.입고사진 : [];
    },
    // only-kind 칸은 그 종류만, 나머지 패널은 확인사진(需跟客户确认 칸 전용)을 뺀다 — 같은 창에 두 번 보이지 않게
    shownPhotos() {
      return this.onlyKind ? this.photos.filter(p => p.종류 === this.kind) : this.photos.filter(p => p.종류 !== '확인사진');
    },
    // 需跟客户确认 칸(kind=확인사진, only-kind) — PC 업로드 버튼을 같이 보인다
    confirmMode() {
      return this.onlyKind && this.kind === '확인사진';
    },
    showSize() {
      return !this.compact && this.kind === '입고사진';
    },
    // 목록 행 버튼은 QR 이 열려 있을 때만, 작업/오류 창 패널은 떠 있는 동안 폴링한다
    // only-kind 칸은 같은 창의 입고사진 패널이 이미 같은 건을 폴링하므로 QR 이 열려 있을 때만
    polling() {
      return !!(this.item && this.item._id) && ((!this.compact && !this.onlyKind) || this.showQr);
    }
  },
  watch: {
    'item._id': {
      immediate: true,
      handler() {
        this.seen = null;
        this.readMsg = '';
        this.readFail = false;
        this.syncSizeFromItem();
        this.inherit();
        this.restartPolling();
      }
    },
    polling() {
      this.restartPolling();
    }
  },
  // 상단 탭(keep-alive)으로 숨은 동안은 사진 폴링을 멈추고, 돌아오면 다시 잇는다
  activated() {
    if (!this.timer) this.restartPolling();
  },
  deactivated() {
    this.stopPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    kindCode(v) { const k = KINDS.find(x => x.value === v); return k ? k.code : 'in'; },
    kindCn(v) { const k = KINDS.find(x => x.value === v); return k ? k.cn : v; },
    syncSizeFromItem() {
      const it = this.item || {};
      this.size = { sizeX: it.sizeX || 0, sizeY: it.sizeY || 0, sizeZ: it.sizeZ || 0, weightKg: it.weightKg || 0 };
      this.unsure = { sizeX: false, sizeY: false, sizeZ: false, weightKg: false };
    },
    // 같은 상품의 예전 입고 사진(상품·포장·사이즈)을 이 건에 붙이고, 사이즈가 비어 있으면 예전 저장값을 칸에 미리 채운다(저장은 담당자가)
    async inherit() {
      const item = this.item;
      if (!this.showSize || !item || !item._id) return;
      try {
        const { data } = await axios.post('/api/importPhoto/inherit', { _id: item._id });
        if (data.요청결과 !== '성공' || item !== this.item) {
          if (data.요청결과 !== '성공') console.error('[입고사진] 이전 입고 사진 불러오기 실패', data.error);
          return;
        }
        if (data.added) {
          this.$set(item, '입고사진', data.입고사진 || this.photos);
          // 붙인 사진을 새로 들어온 사이즈 사진으로 보고 다시 판독하지 않게 기준선에 넣는다
          if (this.seen) (data.입고사진 || []).forEach(p => this.seen.add(p.url));
        }
        const s = data.prevSize;
        const empty = !(this.size.sizeX || this.size.sizeY || this.size.sizeZ || this.size.weightKg);
        if (s && empty) {
          this.size = { sizeX: s.sizeX || 0, sizeY: s.sizeY || 0, sizeZ: s.sizeZ || 0, weightKg: s.weightKg || 0 };
          this.readFail = false;
          this.readMsg = `以前入库尺寸 ${this.size.sizeX}×${this.size.sizeY}×${this.size.sizeZ}cm ${this.size.weightKg}kg，请确认后保存(이전 입고 사이즈 — 확인 후 저장)`;
        }
      } catch (e) {
        console.error('[입고사진] 이전 입고 사진 불러오기 실패', e);
      }
    },
    stopPolling() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    },
    restartPolling() {
      this.stopPolling();
      if (!this.polling) return;
      this.refresh();
      this.timer = setInterval(this.refresh, POLL_MS);
    },
    async openQr() {
      if (!this.item || !this.item._id) return;
      this.issuing = true;
      try {
        const { data } = await axios.post('/api/importPhoto/issue', { _id: this.item._id });
        if (data.요청결과 !== '성공') {
          alert('二维码生成失败(QR 생성 실패): ' + ((data.error && data.error.errorMessage) || ''));
          return;
        }
        const k = KINDS.find(x => x.value === this.kind) || KINDS[0];
        this.qrUrl = `${window.location.origin}/pages/import-photo/${data.id}?e=${data.exp}&t=${data.token}&k=${k.code}`;
        // 오류정정 L — 주소가 길어(토큰 64자) M 이면 한 단계 더 빽빽해져 폰 카메라가 늦게 잡는다(화면 표시용이라 L 로 충분)
        const qr = qrcode(0, 'L');
        qr.addData(this.qrUrl);
        qr.make();
        this.qrDataUrl = qr.createDataURL(6, 4);
        this.showQr = true;
      } catch (e) {
        console.error('[입고사진] QR 발급 실패', e);
        alert('二维码生成失败(QR 생성 실패): ' + e.message);
      } finally {
        this.issuing = false;
      }
    },
    // PC 에서 바로 올리기 — 핸드폰 페이지와 같은 길: S3(import-photo/<건id>/) 직접 업로드 →
    // 이 건의 촬영 토큰(/api/importPhoto/issue)으로 /api/importPhoto/add 에 kind 종류로 붙인다
    async onPcFiles(e) {
      const files = Array.from((e.target && e.target.files) || []);
      if (e.target) e.target.value = '';
      const item = this.item;
      if (!files.length || !item || !item._id) return;
      this.uploading = true;
      try {
        const { data: tk } = await axios.post('/api/importPhoto/issue', { _id: item._id });
        if (tk.요청결과 !== '성공') {
          alert('上传失败(업로드 실패): ' + ((tk.error && tk.error.errorMessage) || ''));
          return;
        }
        const urls = [];
        for (const f of files.slice(0, 20)) urls.push(await uploadImportPhoto(f, item._id));
        const { data } = await axios.post('/api/importPhoto/add', { id: tk.id, e: tk.exp, t: tk.token, 종류: this.kind, urls });
        if (data.요청결과 !== '성공') {
          alert('上传失败(업로드 실패): ' + ((data.error && data.error.errorMessage) || ''));
          return;
        }
        if (item === this.item) this.$set(item, '입고사진', data.입고사진 || this.photos);
        this.$bvToast.toast(`${urls.length}张照片已上传(사진 ${urls.length}장 업로드)`, { title: '上传(업로드)', variant: 'success', solid: true, toaster: 'b-toaster-bottom-left' });
      } catch (err) {
        console.error('[입고사진] PC 업로드 실패', err);
        alert('上传失败(업로드 실패): ' + err.message);
      } finally {
        this.uploading = false;
      }
    },
    async refresh() {
      const item = this.item;
      if (!item || !item._id) return;
      try {
        const { data } = await axios.post('/api/importPhoto/list', { _id: item._id });
        if (data.요청결과 !== '성공' || item !== this.item) return;
        const list = data.입고사진 || [];
        if (list.length !== this.photos.length) this.$set(item, '입고사진', list);
        // 처음 받은 목록은 기준선으로만 삼는다 — 예전 사이즈 사진을 다시 판독하지 않는다
        const isFirst = !this.seen;
        if (isFirst) this.seen = new Set();
        const fresh = list.filter(p => !this.seen.has(p.url));
        fresh.forEach(p => this.seen.add(p.url));
        // 오류 등록 창: 오류사진은 등록 버튼을 누를 때 기존 이미지 목록에 같이 실려 가야 한다
        if (this.kind === '오류사진') {
          // 예전에 등록·처리된 오류의 사진은 다시 싣지 않는다
          const old = (item.입고오류상세 && Array.isArray(item.입고오류상세.이미지)) ? item.입고오류상세.이미지 : [];
          const errUrls = list.filter(p => p.종류 === '오류사진' && !old.includes(p.url)).map(p => p.url);
          if (errUrls.length) this.$emit('error-photos', errUrls);
        }
        if (!isFirst && this.showSize) {
          // 예전 입고에서 붙여 온 사이즈 사진은 판독하지 않는다(예전 저장값을 이미 칸에 채웠다)
          // 창고는 치수(줄자 1~3장)와 무게(저울 1장)를 나눠 찍는다 — 새 사진이 오면 이번 입고 사이즈 사진 전체를 한 번에 합쳐 읽는다
          const sizeNew = fresh.filter(p => p.종류 === '사이즈사진' && !p.이전입고);
          if (sizeNew.length) {
            const all = list.filter(p => p.종류 === '사이즈사진' && !p.이전입고).map(p => p.url);
            this.readSize(all.slice(-READ_MAX));
          }
        }
      } catch (e) {
        console.error('[입고사진] 목록 갱신 실패', e);
      }
    },
    // 사진 삭제 — DB 목록에서만 뺀다(S3 파일은 남는다). 오류 등록 창이면 등록 대기 목록에서도 빼게 알린다
    async removePhoto(p) {
      const item = this.item;
      if (!item || !item._id) return;
      if (!confirm('删除这张照片?(이 사진을 삭제할까요?)')) return;
      this.removing = p.url;
      try {
        const { data } = await axios.post('/api/importPhoto/remove', { _id: item._id, url: p.url });
        if (data.요청결과 !== '성공') {
          alert('删除失败(삭제 실패): ' + ((data.error && data.error.errorMessage) || ''));
          return;
        }
        this.$set(item, '입고사진', data.입고사진 || this.photos.filter(x => x.url !== p.url));
        if (item.입고오류상세 && Array.isArray(item.입고오류상세.이미지)) {
          this.$set(item.입고오류상세, '이미지', item.입고오류상세.이미지.filter(u => u !== p.url));
        }
        this.$emit('photo-removed', p.url);
      } catch (e) {
        console.error('[입고사진] 삭제 실패', e);
        alert('删除失败(삭제 실패): ' + e.message);
      } finally {
        this.removing = '';
      }
    },
    // urls: 사이즈 사진 여러 장(합쳐 판독). 읽힌 값만 칸에 채우고, 못 읽은(null) 칸은 기존 값을 그대로 둔다.
    // 확실하지 않은 값도 채우되 그 칸에 「请确认」 표시 — 칸을 통째로 비우면 무게처럼 확실히 읽힌 값까지 버리게 된다
    async readSize(urls) {
      const item = this.item;
      const seq = ++this.readSeq;
      this.reading = true;
      this.readMsg = '';
      this.readFail = false;
      try {
        const { data } = await axios.post('/api/importPhoto/readSize', { urls });
        if (item !== this.item || seq !== this.readSeq) return;
        const r = data.요청결과 === '성공' ? data.result : null;
        if (!r || !r.ok) {
          if (data.요청결과 !== '성공') console.error('[입고사진] 사이즈 판독 실패', data.error);
          this.readFail = true;
          this.readMsg = '识别失败，请手动输入(판독 실패, 직접 입력해 주세요)';
          return;
        }
        const conf = r.confidence || {};
        const size = { ...this.size };
        const unsure = { ...this.unsure };
        SIZE_FIELDS.forEach(f => {
          const v = r[f.read];
          if (v == null || !(v > 0)) return;
          size[f.key] = v;
          // 값별 확실도가 없는 옛 응답이면 전체 confident 로 본다
          unsure[f.key] = r.confidence ? conf[f.read] !== true : r.confident !== true;
        });
        this.size = size;
        this.unsure = unsure;
        const missing = SIZE_FIELDS.some(f => !this.size[f.key]);
        const anyUnsure = SIZE_FIELDS.some(f => this.unsure[f.key]);
        this.readFail = missing || anyUnsure;
        this.readMsg = missing
          ? '部分识别，请补充后保存(일부만 판독됨, 빈 칸 입력 후 저장)'
          : anyUnsure
            ? '已识别，黄色栏请确认后保存(판독됨, 노란 칸 확인 후 저장)'
            : `已识别 ${this.size.sizeX}×${this.size.sizeY}×${this.size.sizeZ}cm ${this.size.weightKg}kg，请确认后保存(판독됨, 확인 후 저장)`;
      } catch (e) {
        console.error('[입고사진] 사이즈 판독 실패', e);
        if (seq !== this.readSeq) return;
        this.readFail = true;
        this.readMsg = '识别失败，请手动输入(판독 실패, 직접 입력해 주세요)';
      } finally {
        if (seq === this.readSeq) this.reading = false;
      }
    },
    async saveSize() {
      const item = this.item;
      if (!item || !item._id) return;
      this.saving = true;
      try {
        const { data } = await axios.post('/api/reqpurchase/updateVolume', {
          reqpurchaseId: item._id,
          sizeX: this.size.sizeX, sizeY: this.size.sizeY, sizeZ: this.size.sizeZ,
          weightKg: this.size.weightKg,
          saveToProductdb: true
        });
        if (data.요청결과 !== '성공') {
          alert('保存失败(저장 실패): ' + ((data.error && data.error.errorMessage) || ''));
          return;
        }
        ['sizeX', 'sizeY', 'sizeZ', 'weightKg'].forEach(k => this.$set(item, k, Number(this.size[k]) || 0));
        const db = { '저장': '商品DB已同步(상품DB 반영)', '매칭없음': '商品DB无匹配(상품DB 매칭 없음)', '중복': '商品DB多条匹配,未同步(상품DB 중복, 미반영)', '바코드없음': '无条码,商品DB未同步(바코드 없음)' }[data.상품DB] || '';
        this.$bvToast.toast('尺寸已保存(사이즈 저장)' + (db ? ' · ' + db : ''), { title: '保存(저장)', variant: 'success', solid: true, toaster: 'b-toaster-bottom-left' });
        this.readMsg = '';
        this.unsure = { sizeX: false, sizeY: false, sizeZ: false, weightKg: false };
      } catch (e) {
        console.error('[입고사진] 사이즈 저장 실패', e);
        alert('保存失败(저장 실패): ' + e.message);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.ipp__grid { display: flex; flex-wrap: wrap; gap: 6px; }
.ipp__cell { position: relative; width: 72px; height: 72px; border-radius: 4px; overflow: hidden; background: #f1f1f1; }
.ipp__cell img { width: 100%; height: 100%; object-fit: cover; }
.ipp__tag { position: absolute; left: 2px; top: 2px; padding: 0 4px; border-radius: 3px; font-size: 10px; color: #fff; background: #007bff; }
.ipp__tag--err { background: #dc3545; }
.ipp__tag--size { background: #28a745; }
.ipp__tag--cf { background: #fd7e14; }
.ipp__del { position: absolute; right: 2px; top: 2px; width: 20px; height: 20px; padding: 0; border: none; border-radius: 50%; background: rgba(0, 0, 0, .6); color: #fff; font-size: 11px; line-height: 20px; cursor: pointer; }
.ipp__del:disabled { opacity: .4; }
.ipp__prev { position: absolute; right: 2px; bottom: 2px; padding: 0 4px; border-radius: 3px; font-size: 10px; color: #fff; background: #6c757d; }
.ipp__unsure { border-color: #ffc107; box-shadow: 0 0 0 2px rgba(255, 193, 7, .35); }
.ipp__read { position: absolute; left: 2px; bottom: 2px; padding: 0 4px; font-size: 10px; line-height: 1.4; }
</style>
